import * as React from 'react'
import { flow, O, pipe, RA, T, TE } from '@packages/prelude'
import * as domain from '@packages/domain'
import { buildSegments, isAsset, objectUrlOf, remoteUrlOf, setRecording } from '~/components/AudioSegments/state/audioSegments'

export interface UseAudioSegmentsStateArgs {
  meta: domain.Meta.ProductPersonalization.Type
  parameters: ReadonlyArray<domain.IAParameter.Type>
  initialData: O.Option<domain.Api.IALoadPreviewResponse.Type>
  // a restored recording whose stored URL is gone is reported here (the lookup succeeded, the asset didn't)
  onRestoreError: (error: domain.Error.Type) => void
}

type Segments = ReadonlyArray<domain.AudioSegment.Type>

// Fetch the restored recording; a non-ok response means the asset is gone (deleted/expired), not audio.
const fetchBlob = (url: string): TE.TaskEither<domain.Error.Type, Blob> =>
  pipe(
    TE.tryCatch(() => fetch(url), domain.Error.toUnknownError),
    TE.filterOrElse(
      r => r.ok,
      () => domain.Error.toAudioNotFoundError(url)
    ),
    TE.chain(TE.tryCatchK(r => r.blob(), domain.Error.toUnknownError))
  )

export const useAudioSegmentsState = ({
  meta,
  parameters,
  initialData,
  onRestoreError
}: UseAudioSegmentsStateArgs): readonly [Segments, React.Dispatch<React.SetStateAction<Segments>>, () => void] => {
  const initialSegments = React.useMemo(() => buildSegments(meta, parameters, initialData), [meta, parameters, initialData])
  const [segments, setSegments] = React.useState<Segments>(initialSegments)

  // Hydrate audio Blobs for restored uploads / ai-greetings.
  React.useEffect(() => {
    pipe(
      initialSegments,
      RA.filterMap(
        flow(
          O.fromPredicate(isAsset),
          O.chain(seg =>
            pipe(
              remoteUrlOf(seg),
              O.map(url => [seg, url] as const)
            )
          )
        )
      ),
      RA.map(([seg, url]) =>
        pipe(
          fetchBlob(url),
          TE.match(
            error => {
              domain.Error.log(error)()
              onRestoreError(error)
            },
            blob => setSegments(setRecording(seg, blob, URL.createObjectURL(blob)))
          )
        )
      ),
      T.sequenceArray
    )()
  }, [initialSegments, onRestoreError])

  const reset = React.useCallback(
    () =>
      setSegments(prev => {
        pipe(prev, RA.filterMap(objectUrlOf), RA.map(URL.revokeObjectURL))
        return buildSegments(meta, parameters, O.none)
      }),
    [meta, parameters]
  )

  return [segments, setSegments, reset] as const
}
